import React from 'react'
import { useI18nContext } from '../hooks/I18nContext'

const Support: React.FC = () => {
  const { t, tRaw } = useI18nContext()

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-blue-50 to-green-50">
      <div className="relative z-10">
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-6">
                {t('support.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
                {t('support.hero.subtitle')}
              </p>
            </div>
          </section>

          {/* Educational Philosophy */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('support.philosophy.title')}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    icon: '🎯',
                    title: t('support.philosophy.vision.title'),
                    description: t('support.philosophy.vision.description')
                  },
                  {
                    icon: '🛡️',
                    title: t('support.philosophy.safety.title'),
                    description: t('support.philosophy.safety.description')
                  },
                  {
                    icon: '📈',
                    title: t('support.philosophy.effectiveness.title'),
                    description: t('support.philosophy.effectiveness.description')
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-2xl p-8 shadow-lg text-center">
                    <div className="text-4xl mb-4">{item.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Parent Support Features */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('support.control.title')}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {(tRaw('support.parentFeatures') || []).map((feature: any, index: number) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg text-center">
                    <div className="text-4xl mb-4">{['⏰', '📊', '🔒', '📱', '📈', '🎯'][index]}</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Expert Recommendations */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('support.experts.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {(tRaw('support.expertTestimonials') || []).map((expert: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-8">
                    <div className="flex items-center mb-6">
                      <div className="text-4xl mr-4">{['👩‍⚕️', '👨‍🏫'][index]}</div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{['Dr. Indah Sari', 'Prof. Budi Santoso'][index]}</h3>
                        <p className="text-gray-600">{expert.title}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 italic">
                      "{expert.quote}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('support.faq.title')}
              </h2>
              <div className="space-y-6">
                {(tRaw('support.faqItems') || []).map((faq: any, index: number) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                          {faq.question}
                        </h3>
                        <p className="text-gray-600">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                {t('support.contact.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('support.contact.description')}
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="text-3xl mb-4">📧</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('support.contactInfo.email')}</h3>
                  <p className="text-gray-600">support@genius-ai.com</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="text-3xl mb-4">📞</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('support.contactInfo.phone')}</h3>
                  <p className="text-gray-600">+62-21-1234-5678</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="text-3xl mb-4">⏰</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('support.contactInfo.serviceTime')}</h3>
                  <p className="text-gray-600">{t('support.contactInfo.serviceHours')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Parent Guide */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('support.tips.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {(tRaw('support.interactionTips') || []).map((tip: any, index: number) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                    <div className="flex items-start">
                      <div className="text-3xl mr-4">{['👨‍👩‍👧‍👦', '💪', '🎭', '🤝'][index]}</div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          {tip.title}
                        </h3>
                        <p className="text-gray-600">
                          {tip.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default Support
